import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Sparkles, ArrowRight, ArrowLeft, Plus, Trash2, FileDown,
  Loader2, Lock, Check, ChevronRight, User as UserIcon,
} from "lucide-react";
import { toast } from "sonner";
import SEO from "@/components/SEO";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import LanguagePicker from "@/components/LanguagePicker";
import Footer from "@/components/Footer";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = "rl_resume_draft_v1";
const SESSION_KEY = "rl_builder_session";

function getOrCreateSessionId() {
  try {
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id = `bld-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
      sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch (e) { return "anon"; }
}
function trackEvent(event, step, props = {}) {
  try {
    api.post("/builder/events", {
      event,
      step,
      session_id: getOrCreateSessionId(),
      props,
    }).catch(() => {});  // fire-and-forget; analytics must never block UX
  } catch (e) { /* noop */ }
}

const TEMPLATE_OPTIONS = [
  { key: "modern", name: "Aurora Modern", proOnly: false },
  { key: "classic", name: "Heritage Classic", proOnly: true },
  { key: "tech", name: "Vertex Tech", proOnly: true },
  { key: "executive", name: "Apex Executive", proOnly: true },
  { key: "two_column", name: "Atlas Two-Column", proOnly: true },
  { key: "academic", name: "Scholar Academic", proOnly: true },
  { key: "meridian", name: "Meridian Detail", proOnly: true, badge: "NEW" },
  { key: "boulevard", name: "Boulevard Sidebar", proOnly: true, badge: "NEW" },
];

const STEPS = [
  { key: "template", label: "Template" },
  { key: "contact", label: "Contact" },
  { key: "summary", label: "Summary" },
  { key: "experience", label: "Experience" },
  { key: "education", label: "Education" },
  { key: "skills", label: "Skills" },
  { key: "extras", label: "Extras" },
  { key: "preview", label: "Preview & Download" },
];

const EMPTY_DRAFT = {
  template_key: "modern",
  lang: "en",
  payload: {
    name: "",
    title: "",
    contact: { email: "", phone: "+49 30 1234 5678", location: "Berlin, Germany", linkedin: "", github: "", website: "" },
    summary: "",
    experience: [{ company: "", role: "", location: "", start: "", end: "", bullets: [""] }],
    education: [{ institution: "", degree: "", location: "", start: "", end: "", notes: "" }],
    skills: { "Languages & Frameworks": [], "Cloud & DevOps": [], "Data & Tools": [] },
    certifications: [],
    languages: [],
  },
};

function loadFromStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) { return null; }
}
function saveToStorage(d) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(d)); } catch (e) { /* quota or disabled — drop silently */ }
}

function Field({ label, children, hint }) {
  return (
    <label className="block">
      <div className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-1.5">{label}</div>
      {children}
      {hint ? <div className="text-xs text-zinc-500 mt-1">{hint}</div> : null}
    </label>
  );
}
function TextInput(props) {
  return <input {...props} className={`w-full border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:border-zinc-900 ${props.className || ""}`} />;
}
function TextArea(props) {
  return <textarea {...props} className={`w-full border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:border-zinc-900 resize-none ${props.className || ""}`} />;
}

function StepIndicator({ currentStep, onJump }) {
  return (
    <nav className="flex items-center gap-1 overflow-x-auto pb-1" aria-label="Progress" data-testid="builder-step-indicator">
      {STEPS.map((s, i) => {
        const active = i === currentStep;
        const done = i < currentStep;
        return (
          <button
            key={s.key}
            type="button"
            onClick={() => onJump(i)}
            data-testid={`builder-step-${s.key}`}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-mono uppercase tracking-widest border whitespace-nowrap transition-colors ${
              active ? "bg-zinc-900 text-white border-zinc-900" :
              done ? "bg-white text-zinc-700 border-zinc-300 hover:border-zinc-500" :
              "bg-zinc-50 text-zinc-400 border-zinc-200"
            }`}
          >
            {done ? <Check className="w-3 h-3" /> : `${i + 1}.`}
            {s.label}
            {i < STEPS.length - 1 ? <ChevronRight className="w-3 h-3 opacity-50" /> : null}
          </button>
        );
      })}
    </nav>
  );
}

export default function Builder() {
  const { user } = useAuth();
  const isPro = !!user?.is_pro;
  const [params] = useSearchParams();
  const [step, setStep] = useState(0);
  const [draft, setDraft] = useState(() => {
    const fromStorage = loadFromStorage();
    if (fromStorage) return fromStorage;
    const initialTemplate = params.get("template") || "modern";
    return { ...EMPTY_DRAFT, template_key: TEMPLATE_OPTIONS.some(t => t.key === initialTemplate) ? initialTemplate : "modern" };
  });
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [aiLoading, setAiLoading] = useState({});

  // Auto-save to localStorage on every change
  useEffect(() => { saveToStorage(draft); }, [draft]);

  // Track step view on each step change
  useEffect(() => {
    trackEvent("step_view", STEPS[step].key);
  }, [step]);

  // Sync to Mongo every 30s if logged in
  useEffect(() => {
    if (!user) return;
    const id = setInterval(async () => {
      try {
        setSaving(true);
        await api.post("/builder/drafts", draft);
      } catch (e) {
        // silently ignore — localStorage is the source of truth
      } finally { setSaving(false); }
    }, 30000);
    return () => clearInterval(id);
  }, [user, draft]);

  // On first login, fetch remote draft if local is empty/never-saved
  useEffect(() => {
    if (!user) return;
    const local = loadFromStorage();
    if (local && local.payload?.name) return; // user has local content, don't overwrite
    (async () => {
      try {
        const { data } = await api.get("/builder/drafts/latest");
        if (data?.payload) {
          setDraft({ template_key: data.template_key || "modern", lang: data.lang || "en", payload: data.payload });
        }
      } catch (e) { /* 404 — no draft yet, that's fine */ }
    })();
  }, [user]);

  const update = useCallback((patch) => {
    setDraft((d) => ({ ...d, payload: { ...d.payload, ...patch } }));
  }, []);
  const updateContact = (key, value) => update({ contact: { ...draft.payload.contact, [key]: value } });
  const setTemplate = (key) => setDraft((d) => ({ ...d, template_key: key }));
  const setLang = (code) => {
    setDraft((d) => ({ ...d, lang: code }));
    try { localStorage.setItem("rl_lang", code); } catch (e) { /* ignore */ }
  };

  const addExperience = () => update({ experience: [...draft.payload.experience, { company: "", role: "", location: "", start: "", end: "", bullets: [""] }] });
  const removeExperience = (i) => update({ experience: draft.payload.experience.filter((_, idx) => idx !== i) });
  const updateExperience = (i, patch) => update({ experience: draft.payload.experience.map((e, idx) => idx === i ? { ...e, ...patch } : e) });
  const updateExperienceBullet = (expIdx, bIdx, value) => updateExperience(expIdx, { bullets: draft.payload.experience[expIdx].bullets.map((b, j) => j === bIdx ? value : b) });
  const addBullet = (i) => updateExperience(i, { bullets: [...draft.payload.experience[i].bullets, ""] });
  const removeBullet = (expIdx, bIdx) => updateExperience(expIdx, { bullets: draft.payload.experience[expIdx].bullets.filter((_, j) => j !== bIdx) });

  const addEducation = () => update({ education: [...draft.payload.education, { institution: "", degree: "", location: "", start: "", end: "", notes: "" }] });
  const removeEducation = (i) => update({ education: draft.payload.education.filter((_, idx) => idx !== i) });
  const updateEducation = (i, patch) => update({ education: draft.payload.education.map((e, idx) => idx === i ? { ...e, ...patch } : e) });

  const updateSkillCategory = (oldCat, newCat) => {
    const skills = { ...draft.payload.skills };
    if (oldCat !== newCat) { skills[newCat] = skills[oldCat]; delete skills[oldCat]; }
    update({ skills });
  };
  const updateSkillList = (cat, csv) => {
    const skills = { ...draft.payload.skills, [cat]: csv.split(",").map(s => s.trim()).filter(Boolean) };
    update({ skills });
  };
  const removeSkillCategory = (cat) => {
    const skills = { ...draft.payload.skills };
    delete skills[cat];
    update({ skills });
  };
  const addSkillCategory = () => {
    const newName = `Category ${Object.keys(draft.payload.skills).length + 1}`;
    update({ skills: { ...draft.payload.skills, [newName]: [] } });
  };

  const suggestBullets = async (expIdx) => {
    if (!isPro) {
      toast.error("AI bullet suggestions are a Pro feature.", { description: "Upgrade to unlock." });
      return;
    }
    const exp = draft.payload.experience[expIdx];
    if (!exp.role) {
      toast.error("Add a role title first so the AI knows what to write.");
      return;
    }
    setAiLoading({ ...aiLoading, [expIdx]: true });
    trackEvent("ai_suggest", "experience", { role: exp.role });
    try {
      const { data } = await api.post("/builder/suggest-bullets", {
        role: exp.role,
        company: exp.company,
        context: draft.payload.title || draft.payload.summary,
        lang: draft.lang || "en",
      });
      const newBullets = (data?.bullets || []).slice(0, 6);
      const existing = exp.bullets.filter(b => b.trim());
      updateExperience(expIdx, { bullets: [...existing, ...newBullets] });
      toast.success(`${newBullets.length} bullets added — edit as needed`);
    } catch (e) {
      toast.error("AI suggestion failed", { description: e?.response?.data?.detail || e.message });
    } finally { setAiLoading({ ...aiLoading, [expIdx]: false }); }
  };

  const downloadDocx = async () => {
    const tplKey = draft.template_key;
    const isProTpl = TEMPLATE_OPTIONS.find(t => t.key === tplKey)?.proOnly;
    if (isProTpl && !isPro) {
      toast.error("This template is Pro-only", { description: "Pick Modern for free, or upgrade." });
      return;
    }
    setDownloading(true);
    trackEvent("download_started", "preview", { template: tplKey, is_pro: isPro });
    try {
      const res = await api.post("/builder/build-docx", draft, { responseType: "blob" });
      const disp = res.headers?.["content-disposition"] || "";
      let filename = `ResumeLab_${tplKey}.docx`;
      const m = /filename="([^"]+)"/.exec(disp);
      if (m) filename = m[1];
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document" }));
      const a = document.createElement("a"); a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      toast.success("Resume downloaded!");
      trackEvent("download_success", "preview", { template: tplKey });
    } catch (e) {
      toast.error("Download failed", { description: e?.response?.data?.detail || e.message });
    } finally { setDownloading(false); }
  };

  // ----- step renderers -----
  const renderStep = () => {
    const p = draft.payload;
    switch (STEPS[step].key) {
      case "template":
        return (
          <div className="space-y-6" data-testid="builder-step-template">
            <p className="text-zinc-600">Pick a visual style. <strong className="text-zinc-900">Modern is free.</strong> The others unlock with Pro.</p>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
              {TEMPLATE_OPTIONS.map((t) => {
                const locked = t.proOnly && !isPro;
                const active = draft.template_key === t.key;
                return (
                  <button
                    key={t.key} type="button"
                    onClick={() => !locked && setTemplate(t.key)}
                    data-testid={`builder-template-${t.key}`}
                    className={`p-5 border text-left transition-colors ${active ? "border-[#EF4444] bg-red-50" : "border-zinc-300 hover:border-zinc-500"} ${locked ? "opacity-60" : ""}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="font-semibold text-sm text-zinc-900">{t.name}</div>
                      {locked ? <Lock className="w-3.5 h-3.5 text-zinc-400" /> : active ? <Check className="w-3.5 h-3.5 text-[#EF4444]" /> : null}
                    </div>
                    {locked ? <div className="text-xs text-zinc-500 mt-1">Pro only</div> : null}
                  </button>
                );
              })}
            </div>
          </div>
        );

      case "contact":
        return (
          <div className="space-y-5" data-testid="builder-step-contact">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="Full Name"><TextInput data-testid="builder-name" value={p.name} onChange={(e) => update({ name: e.target.value })} placeholder="Amani Okonkwo" /></Field>
              <Field label="Professional Title"><TextInput data-testid="builder-title" value={p.title} onChange={(e) => update({ title: e.target.value })} placeholder="Senior Backend Engineer" /></Field>
              <Field label="Email"><TextInput type="email" data-testid="builder-email" value={p.contact.email} onChange={(e) => updateContact("email", e.target.value)} placeholder="amani@example.com" /></Field>
              <Field label="Phone"><TextInput data-testid="builder-phone" value={p.contact.phone} onChange={(e) => updateContact("phone", e.target.value)} placeholder="+254 712 345 678" /></Field>
              <Field label="Location"><TextInput data-testid="builder-location" value={p.contact.location} onChange={(e) => updateContact("location", e.target.value)} placeholder="Nairobi, Kenya" /></Field>
              <Field label="LinkedIn"><TextInput value={p.contact.linkedin} onChange={(e) => updateContact("linkedin", e.target.value)} placeholder="linkedin.com/in/..." /></Field>
              <Field label="GitHub (optional)"><TextInput value={p.contact.github} onChange={(e) => updateContact("github", e.target.value)} placeholder="github.com/..." /></Field>
              <Field label="Website (optional)"><TextInput value={p.contact.website} onChange={(e) => updateContact("website", e.target.value)} /></Field>
            </div>
          </div>
        );

      case "summary":
        return (
          <div className="space-y-4" data-testid="builder-step-summary">
            <Field label="Professional Summary" hint="3-4 sentences. Lead with role + scale + sector.">
              <TextArea rows={6} data-testid="builder-summary" value={p.summary} onChange={(e) => update({ summary: e.target.value })} placeholder="Senior backend engineer with 8 years across East African fintech…" />
            </Field>
          </div>
        );

      case "experience":
        return (
          <div className="space-y-6" data-testid="builder-step-experience">
            {p.experience.map((exp, i) => (
              <div key={i} className="border border-zinc-300 p-5 space-y-4" data-testid={`builder-experience-${i}`}>
                <div className="flex items-center justify-between">
                  <div className="text-xs font-mono uppercase tracking-widest text-zinc-500">Role #{i + 1}</div>
                  {p.experience.length > 1 ? (
                    <button type="button" onClick={() => removeExperience(i)} className="text-zinc-400 hover:text-red-600" aria-label="Remove role" data-testid={`builder-experience-remove-${i}`}><Trash2 className="w-4 h-4" /></button>
                  ) : null}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label="Role"><TextInput value={exp.role} onChange={(e) => updateExperience(i, { role: e.target.value })} placeholder="Staff Engineer" /></Field>
                  <Field label="Company"><TextInput value={exp.company} onChange={(e) => updateExperience(i, { company: e.target.value })} placeholder="Safaricom PLC" /></Field>
                  <Field label="Location"><TextInput value={exp.location} onChange={(e) => updateExperience(i, { location: e.target.value })} placeholder="Nairobi, Kenya" /></Field>
                  <div className="grid grid-cols-2 gap-2">
                    <Field label="Start"><TextInput value={exp.start} onChange={(e) => updateExperience(i, { start: e.target.value })} placeholder="Mar 2022" /></Field>
                    <Field label="End"><TextInput value={exp.end} onChange={(e) => updateExperience(i, { end: e.target.value })} placeholder="Present" /></Field>
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-xs font-mono uppercase tracking-widest text-zinc-500">Bullets</div>
                    <button type="button" onClick={() => suggestBullets(i)} disabled={aiLoading[i]} data-testid={`builder-ai-suggest-${i}`} className={`text-xs inline-flex items-center gap-1.5 px-2.5 py-1 transition-colors ${isPro ? "bg-zinc-900 text-white hover:bg-black" : "bg-zinc-100 text-zinc-500 border border-zinc-200"} disabled:opacity-60`}>
                      {aiLoading[i] ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                      AI suggest {!isPro ? <Lock className="w-3 h-3" /> : null}
                    </button>
                  </div>
                  <div className="space-y-2">
                    {exp.bullets.map((b, j) => (
                      <div key={j} className="flex items-start gap-2">
                        <span className="text-zinc-400 pt-2.5">•</span>
                        <TextArea rows={2} value={b} onChange={(e) => updateExperienceBullet(i, j, e.target.value)} placeholder="Led the M-Pesa core re-architecture; cut p99 latency 62%." />
                        {exp.bullets.length > 1 ? (
                          <button type="button" onClick={() => removeBullet(i, j)} className="text-zinc-400 hover:text-red-600 pt-2.5" aria-label="Remove bullet"><Trash2 className="w-3.5 h-3.5" /></button>
                        ) : null}
                      </div>
                    ))}
                    <button type="button" onClick={() => addBullet(i)} className="inline-flex items-center gap-1.5 text-xs text-zinc-600 hover:text-zinc-900"><Plus className="w-3.5 h-3.5" /> Add bullet</button>
                  </div>
                </div>
              </div>
            ))}
            <button type="button" onClick={addExperience} data-testid="builder-experience-add" className="w-full border-2 border-dashed border-zinc-300 hover:border-zinc-500 hover:text-zinc-900 text-zinc-500 py-3 text-sm font-semibold inline-flex items-center justify-center gap-2"><Plus className="w-4 h-4" /> Add another role</button>
          </div>
        );

      case "education":
        return (
          <div className="space-y-6" data-testid="builder-step-education">
            {p.education.map((edu, i) => (
              <div key={i} className="border border-zinc-300 p-5 space-y-4">
                <div className="flex items-center justify-between">
                  <div className="text-xs font-mono uppercase tracking-widest text-zinc-500">Education #{i + 1}</div>
                  {p.education.length > 1 ? (
                    <button type="button" onClick={() => removeEducation(i)} className="text-zinc-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                  ) : null}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label="Degree"><TextInput value={edu.degree} onChange={(e) => updateEducation(i, { degree: e.target.value })} placeholder="B.Sc. Computer Science" /></Field>
                  <Field label="Institution"><TextInput value={edu.institution} onChange={(e) => updateEducation(i, { institution: e.target.value })} placeholder="University of Nairobi" /></Field>
                  <Field label="Location"><TextInput value={edu.location} onChange={(e) => updateEducation(i, { location: e.target.value })} /></Field>
                  <div className="grid grid-cols-2 gap-2">
                    <Field label="Start"><TextInput value={edu.start} onChange={(e) => updateEducation(i, { start: e.target.value })} placeholder="2013" /></Field>
                    <Field label="End"><TextInput value={edu.end} onChange={(e) => updateEducation(i, { end: e.target.value })} placeholder="2017" /></Field>
                  </div>
                  <Field label="Notes (Honors / GPA)"><TextInput value={edu.notes} onChange={(e) => updateEducation(i, { notes: e.target.value })} placeholder="First Class Honours" /></Field>
                </div>
              </div>
            ))}
            <button type="button" onClick={addEducation} className="w-full border-2 border-dashed border-zinc-300 hover:border-zinc-500 hover:text-zinc-900 text-zinc-500 py-3 text-sm font-semibold inline-flex items-center justify-center gap-2"><Plus className="w-4 h-4" /> Add another</button>
          </div>
        );

      case "skills":
        return (
          <div className="space-y-4" data-testid="builder-step-skills">
            <p className="text-sm text-zinc-600">Group skills into 2-4 categories. Comma-separated values per row.</p>
            {Object.entries(p.skills).map(([cat, items]) => (
              <div key={cat} className="border border-zinc-300 p-4 grid grid-cols-1 md:grid-cols-[1fr_2fr_auto] gap-3 items-start">
                <Field label="Category"><TextInput value={cat} onChange={(e) => updateSkillCategory(cat, e.target.value)} /></Field>
                <Field label="Skills (comma-separated)"><TextInput value={items.join(", ")} onChange={(e) => updateSkillList(cat, e.target.value)} placeholder="Python, Go, FastAPI" /></Field>
                <button type="button" onClick={() => removeSkillCategory(cat)} className="text-zinc-400 hover:text-red-600 md:mt-7"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
            <button type="button" onClick={addSkillCategory} className="w-full border-2 border-dashed border-zinc-300 hover:border-zinc-500 hover:text-zinc-900 text-zinc-500 py-3 text-sm font-semibold inline-flex items-center justify-center gap-2"><Plus className="w-4 h-4" /> Add category</button>
          </div>
        );

      case "extras":
        return (
          <div className="space-y-6" data-testid="builder-step-extras">
            <Field label="Certifications (one per line)" hint="e.g. AWS Solutions Architect (2024)">
              <TextArea rows={4} value={(p.certifications || []).join("\n")} onChange={(e) => update({ certifications: e.target.value.split("\n").map(s => s.trim()).filter(Boolean) })} />
            </Field>
            <Field label="Languages (one per line)" hint="e.g. English (Native)">
              <TextArea rows={4} value={(p.languages || []).join("\n")} onChange={(e) => update({ languages: e.target.value.split("\n").map(s => s.trim()).filter(Boolean) })} />
            </Field>
          </div>
        );

      case "preview":
        return (
          <div className="space-y-6" data-testid="builder-step-preview">
            <div className="bg-zinc-50 border border-zinc-200 p-6">
              <div className="flex items-start gap-4">
                <UserIcon className="w-8 h-8 text-zinc-400" />
                <div className="flex-1">
                  <h3 className="font-display font-bold text-xl text-zinc-900">{p.name || "Your name"}</h3>
                  <p className="text-sm text-zinc-600 mt-0.5">{p.title || "Your title"}</p>
                  <p className="text-xs text-zinc-500 mt-2">
                    {p.contact.email || "email"} · {p.contact.phone || "phone"} · {p.contact.location || "location"}
                  </p>
                </div>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-3 text-xs">
                <div className="bg-white border border-zinc-200 px-3 py-2"><span className="text-zinc-500">Template:</span> <strong className="text-zinc-900">{draft.template_key}</strong></div>
                <div className="bg-white border border-zinc-200 px-3 py-2"><span className="text-zinc-500">Roles:</span> <strong className="text-zinc-900">{p.experience.filter(e => e.role).length}</strong></div>
                <div className="bg-white border border-zinc-200 px-3 py-2"><span className="text-zinc-500">Education:</span> <strong className="text-zinc-900">{p.education.filter(e => e.degree).length}</strong></div>
                <div className="bg-white border border-zinc-200 px-3 py-2"><span className="text-zinc-500">Skills:</span> <strong className="text-zinc-900">{Object.values(p.skills).flat().length}</strong></div>
              </div>
            </div>
            <div className="border border-zinc-200 p-5">
              <LanguagePicker
                value={draft.lang || "en"}
                onChange={setLang}
                label="Resume language"
                testIdPrefix="builder-lang"
              />
              <p className="mt-2 text-[11px] text-zinc-500 leading-snug">
                Section headings ("Berufserfahrung", "Experiencia", etc.) translate in the Word file. Your typed content stays as you wrote it — switch language and re-download to see.
              </p>
            </div>
            <button type="button" onClick={downloadDocx} disabled={downloading} data-testid="builder-download" className="w-full bg-[#EF4444] hover:bg-[#DC2626] text-white py-4 text-base font-semibold inline-flex items-center justify-center gap-2 transition-colors disabled:opacity-60">
              {downloading ? <Loader2 className="w-5 h-5 animate-spin" /> : <FileDown className="w-5 h-5" />}
              {downloading ? "Generating Word doc…" : `Download as Word (${draft.template_key} · ${(draft.lang || "en").toUpperCase()})`}
            </button>
            {!isPro ? (
              <Link to="/pricing" className="block text-center text-sm text-zinc-600 hover:text-zinc-900">
                <Lock className="w-3.5 h-3.5 inline mr-1" /> Want a different template? Upgrade to Pro
              </Link>
            ) : null}
          </div>
        );

      default: return null;
    }
  };

  return (
    <main className="min-h-screen bg-gradient-to-br from-blue-50 via-fuchsia-50 to-amber-50" data-testid="builder-page">
      <SEO
        title="Free Resume Builder — Step-by-Step | ResumeLab"
        description="Build a recruiter-tested resume in 10 minutes. Step-by-step wizard, AI bullet suggestions (Pro), 6 visual templates. Free to start, free to download in Modern."
        url="https://resumelab.co.ke/builder"
      />
      <div className="max-w-5xl mx-auto px-6 lg:px-10 py-8 lg:py-12">
        <header className="mb-10">
          <div className="overline mb-3">◆ RESUME BUILDER</div>
          <h1 className="font-display font-extrabold text-4xl lg:text-5xl tracking-tight text-zinc-900">
            Build it. Download it. Done.
          </h1>
          <p className="text-zinc-600 mt-3 max-w-2xl">
            Step-by-step. Auto-saves as you go. {user ? "Synced across your devices." : <Link to="/login" className="underline">Sign in to sync across devices →</Link>}
            {saving ? <span className="ml-2 text-xs text-zinc-400">Saving…</span> : null}
          </p>
        </header>

        <div className="mb-10">
          <StepIndicator currentStep={step} onJump={setStep} />
        </div>

        <section className="min-h-[300px]" data-testid="builder-step-content">
          {renderStep()}
        </section>

        <div className="mt-12 pt-6 border-t border-zinc-200 flex items-center justify-between" data-testid="builder-nav">
          <button type="button" disabled={step === 0} onClick={() => setStep((s) => Math.max(0, s - 1))} data-testid="builder-prev" className="inline-flex items-center gap-2 text-sm text-zinc-700 hover:text-zinc-900 disabled:opacity-40">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          {step < STEPS.length - 1 ? (
            <button type="button" onClick={() => setStep((s) => Math.min(STEPS.length - 1, s + 1))} data-testid="builder-next" className="inline-flex items-center gap-2 bg-zinc-900 hover:bg-black text-white px-6 py-2.5 text-sm font-semibold transition-colors">
              Continue <ArrowRight className="w-4 h-4" />
            </button>
          ) : null}
        </div>
      </div>
      <Footer />
    </main>
  );
}
